import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loading } from "@alm/flow-ds/components";
import Definition from "./Definition";
import useDefinitionsList from "hooks/FormDesigner/useDefinitionsList";
import api from "lib/api";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import CardColumn from "components/CardColumn";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";

interface Props {
	search: string;
	setAlert: React.Dispatch<React.SetStateAction<string>>;
	alert: string;
}

export type DefinitionType = {
	id: string;
	formLabel: string;
	resource: string;
};

function DefinitionsList({ search, setAlert, alert }: Props) {
	const { t } = useTranslation();
	const [init, setInit] = useState(false);
	const { state, setData, ready, loading } = useDefinitionsList();

	const { limit, name } = state;

	const [currentPage, setCurrentPage] = useState(1);

	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	const disableOnLoad = state.loading ? "disabled" : "";

	useEffect(() => {
		if (!init) {
			// Load definitions from API
			api.definitions
				.getDefinitionsList({ offset: 0, limit: 10 })
				.then(results => {
					setData(results);
					setTotal(results.totalCount);
					ready();
					setInit(true);
				})
				.catch(e => console.error(e));
		}
	}, [init, ready, setData]);

	useEffect(() => {
		if (init) {
			loading();
			setCurrentPage(1);
			// Load definitions from API with search query
			api.definitions
				.getDefinitionsList({ offset: 0, limit: 10, name: search })
				.then(results => {
					setData(results);
					setTotal(results.totalCount);
					ready();
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, alert]);

	const pageData = useCallback(
		(page: number) => {
			loading();
			// Get next page from api
			api.definitions
				.getDefinitionsList({
					offset: (page - 1) * limit,
					limit,
					name,
				})
				.then(res => {
					setData(res);
					ready();
				})
				.catch(e => console.error(e));
			setCurrentPage(page);
		},
		[setData, loading, ready, limit, name]
	);

	if (!init) return <Loading className="mt-4 elem__loader--md" />;
	return (
		<>
			{state.results.length ? (
				<>
					<div
						className={`list-group-heading list-group-heading--position ${disableOnLoad}`}>
						<CardColumn width="lg">
							{t("formDesigner.labels.formId")}
						</CardColumn>
						<CardColumn width="xl">
							{t("formDesigner.labels.entity")}
						</CardColumn>
						<CardColumn width="fill">
							{t("formDesigner.labels.formLabel")}
						</CardColumn>
						<CardColumn className="form-definition-card__menu" />
					</div>
					<ListGroup className="form-definitions-group">
						{state.results.map(
							(definition: DefinitionType, index: number) => {
								const definitionId = definition.id.substring(
									definition.id.indexOf("definitions/") +
										"definitions/".length
								);
								const definitionEntity =
									definition.resource === null
										? "No entity"
										: definition.resource.substring(
												definition.resource.indexOf(
													"resources/"
												) + "resources/".length
										  );
								return (
									<ListGroupItem
										className={`form-definition-card ${disableOnLoad}`}
										key={`form-definition-card__${index}`}>
										<Definition
											definitionId={definitionId}
											definitionEntity={definitionEntity}
											definition={definition}
											setAlert={setAlert}
										/>
									</ListGroupItem>
								);
							}
						)}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-definitions">
						{t(
							search.trim() !== ""
								? "formDesigner.text.noSearch"
								: "formDesigner.text.noDefinitions"
						)}
					</p>
				)
			)}

			{total !== undefined && total > limit && (
				<PaginationBar
					currentPage={currentPage}
					limit={limit}
					total={total}
					paginationLimit={paginationLimit}
					onPageChange={page => {
						pageData(page);
					}}
					loading={state.loading}
				/>
			)}
		</>
	);
}

export default DefinitionsList;
